import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Users, Sparkles, RefreshCw, Lightbulb, Zap, Drama, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ShareResult } from '@/components/social/ShareButtons';

type GamePhase = 'landing' | 'setup' | 'context' | 'playing' | 'twist' | 'end';

interface AIContext {
  title: string;
  setting: string;
  characters: string[];
  constraints: string[];
  twist: string;
}

interface GameState {
  players: string[];
  context: string;
  constraints: string[];
  currentTwist: string;
  sceneNumber: number;
  timeRemaining: number;
}

// Fallback content pools (used if AI fails)
const fallbackContexts = [
  {
    title: "Le Dernier Train",
    setting: "Quai de gare, 23h47. Le dernier train pour Paris vient d'être annoncé.",
    characters: ["Un voyageur pressé", "Un contrôleur fatigué", "Une musicienne de rue"],
    constraints: ["🎭 Interdiction de dire 'oui' ou 'non'", "🔄 Chaque réplique doit contenir une question"],
    twist: "⚡ PLOT TWIST : Quelqu'un vient d'entrer... c'est une célébrité !"
  },
  {
    title: "Réunion de Famille",
    setting: "Dimanche midi. Le repas de famille bat son plein chez Mamie Josette.",
    characters: ["L'oncle qui sait tout", "Le cousin végétarien", "La tante commère"],
    constraints: ["🎵 Terminer chaque phrase en chantant le dernier mot", "💬 Utiliser un accent étranger"],
    twist: "💡 RÉVÉLATION : Un des personnages est en fait un agent secret !"
  },
  {
    title: "Ascenseur en Panne",
    setting: "Un ascenseur bloqué entre deux étages d'un immeuble de bureaux.",
    characters: ["Un PDG stressé", "Un livreur de pizzas", "Une coach de vie"],
    constraints: ["🤫 Parler en chuchotant", "😱 Avoir peur de quelque chose d'invisible"],
    twist: "🎭 SWITCH : Les joueurs échangent leurs personnages !"
  }
];

const Impro = () => {
  const { toast } = useToast();
  const [phase, setPhase] = useState<GamePhase>('landing');
  const [isGenerating, setIsGenerating] = useState(false);
  const [gameState, setGameState] = useState<GameState>({
    players: ['', ''],
    context: '',
    constraints: [],
    currentTwist: '',
    sceneNumber: 1,
    timeRemaining: 120
  });
  const [currentContext, setCurrentContext] = useState<AIContext | null>(null);

  const addPlayer = () => {
    if (gameState.players.length < 6) {
      setGameState(prev => ({ ...prev, players: [...prev.players, ''] }));
    }
  };

  const removePlayer = (index: number) => {
    if (gameState.players.length > 2) {
      setGameState(prev => ({
        ...prev,
        players: prev.players.filter((_, i) => i !== index)
      }));
    }
  };

  const updatePlayer = (index: number, name: string) => {
    setGameState(prev => ({
      ...prev,
      players: prev.players.map((p, i) => i === index ? name : p)
    }));
  };

  const generateContext = useCallback(async () => {
    setIsGenerating(true);
    
    try {
      const playerCount = gameState.players.filter(p => p.trim()).length || 3;
      
      const { data, error } = await supabase.functions.invoke('generate-impro-context', {
        body: { playerCount }
      });

      if (error) throw error;

      if (data && data.title && data.setting && data.characters) {
        const aiContext: AIContext = {
          title: data.title,
          setting: data.setting,
          characters: data.characters.slice(0, playerCount),
          constraints: data.constraints || [],
          twist: data.twist || "⚡ Un événement inattendu survient !"
        };
        
        setCurrentContext(aiContext);
        setGameState(prev => ({
          ...prev,
          context: aiContext.title,
          constraints: aiContext.constraints
        }));
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("AI generation failed, using fallback:", error);
      
      // Use fallback
      const fallback = fallbackContexts[Math.floor(Math.random() * fallbackContexts.length)];
      setCurrentContext(fallback);
      setGameState(prev => ({
        ...prev,
        context: fallback.title,
        constraints: fallback.constraints
      }));
      
      toast({
        title: "Contexte généré localement",
        description: "L'IA n'était pas disponible, mais on a un super scénario quand même !",
        variant: "default"
      });
    } finally {
      setIsGenerating(false);
    }
  }, [gameState.players, toast]);

  const startGame = async () => {
    const validPlayers = gameState.players.filter(p => p.trim());
    if (validPlayers.length >= 2) {
      setGameState(prev => ({ ...prev, players: validPlayers }));
      setPhase('context');
      await generateContext();
    }
  };

  const startScene = () => {
    setPhase('playing');
  };

  const [isGeneratingTwist, setIsGeneratingTwist] = useState(false);

  const triggerTwist = useCallback(async () => {
    setIsGeneratingTwist(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-impro-twist', {
        body: { 
          context: currentContext?.setting || '',
          characters: currentContext?.characters || []
        }
      });

      if (error) throw error;

      if (data?.twist) {
        setGameState(prev => ({ ...prev, currentTwist: data.twist }));
      } else {
        throw new Error("No twist received");
      }
    } catch (error) {
      console.error("AI twist generation failed, using fallback:", error);
      // Fallback to pre-generated twist
      const fallbackTwist = currentContext?.twist || "⚡ Un événement inattendu survient !";
      setGameState(prev => ({ ...prev, currentTwist: fallbackTwist }));
    } finally {
      setIsGeneratingTwist(false);
      setPhase('twist');
    }
  }, [currentContext]);

  const continueAfterTwist = () => {
    setPhase('playing');
  };

  const nextScene = () => {
    generateContext();
    setGameState(prev => ({ 
      ...prev, 
      sceneNumber: prev.sceneNumber + 1,
      currentTwist: ''
    }));
    setPhase('context');
  };

  const endGame = () => {
    setPhase('end');
  };

  const resetGame = () => {
    setPhase('landing');
    setGameState({
      players: ['', ''],
      context: '',
      constraints: [],
      currentTwist: '',
      sceneNumber: 1,
      timeRemaining: 120
    });
    setCurrentContext(null);
  };

  return (
    <>
      <Helmet>
        <title>Impro Théâtrale IA - SuperSociety.ai</title>
        <meta name="description" content="Impro théâtrale guidée par IA. Contextes, contraintes et rebondissements générés en temps réel. 2-6 joueurs." />
      </Helmet>

      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, hsl(var(--seer) / 0.3) 0%, hsl(var(--accent) / 0.1) 40%, transparent 70%)',
            }}
          />
          <div 
            className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full opacity-15 animate-pulse"
            style={{
              background: 'radial-gradient(circle, hsl(var(--moon) / 0.4) 0%, transparent 70%)',
              animationDuration: '5s',
            }}
          />
        </div>

        {/* Back button */}
        <Link 
          to="/" 
          className="absolute top-4 left-4 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 text-muted-foreground hover:text-foreground hover:border-seer/50 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Plateforme</span>
        </Link>

        <div className="relative z-10 container mx-auto px-6 py-20">
          {phase === 'landing' && (
            <div className="max-w-2xl mx-auto text-center space-y-8 animate-fade-in">
              <div className="text-7xl mb-6">🎭</div>
              <h1 className="font-display text-4xl md:text-6xl text-foreground">
                Impro <span className="text-seer">Théâtrale</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-md mx-auto">
                L'IA devient votre metteur en scène : contextes, contraintes et rebondissements en temps réel !
              </p>
              
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto py-8">
                <div className="text-center space-y-2">
                  <Lightbulb className="w-8 h-8 text-seer mx-auto" />
                  <p className="text-xs text-muted-foreground">Contextes</p>
                </div>
                <div className="text-center space-y-2">
                  <Zap className="w-8 h-8 text-moon mx-auto" />
                  <p className="text-xs text-muted-foreground">Contraintes</p>
                </div>
                <div className="text-center space-y-2">
                  <Sparkles className="w-8 h-8 text-accent mx-auto" />
                  <p className="text-xs text-muted-foreground">Rebondissements</p>
                </div>
              </div>

              <Button 
                onClick={() => setPhase('setup')}
                size="lg"
                className="bg-seer hover:bg-seer/90 text-background font-display text-lg px-8"
              >
                <Play className="w-5 h-5 mr-2" />
                Commencer
              </Button>

              <p className="text-sm text-muted-foreground">2-6 joueurs • Aucune préparation</p>
            </div>
          )}

          {phase === 'setup' && (
            <div className="max-w-md mx-auto space-y-8 animate-fade-in">
              <div className="text-center space-y-4">
                <div className="text-5xl">🎭</div>
                <h2 className="font-display text-3xl text-foreground">Les Improvisateurs</h2>
                <p className="text-muted-foreground">Qui monte sur scène ?</p>
              </div>

              <div className="space-y-3">
                {gameState.players.map((player, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={player}
                      onChange={(e) => updatePlayer(index, e.target.value)}
                      placeholder={`Joueur ${index + 1}`}
                      className="bg-background/50 border-seer/30 focus:border-seer"
                    />
                    {gameState.players.length > 2 && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removePlayer(index)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        ×
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {gameState.players.length < 6 && (
                <Button 
                  variant="outline" 
                  onClick={addPlayer}
                  className="w-full border-dashed border-seer/30 text-seer hover:bg-seer/10"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Ajouter un joueur
                </Button>
              )}

              <Button 
                onClick={startGame}
                disabled={gameState.players.filter(p => p.trim()).length < 2}
                className="w-full bg-seer hover:bg-seer/90 text-background font-display"
              >
                Générer le contexte
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {phase === 'context' && isGenerating && (
            <div className="max-w-2xl mx-auto text-center space-y-8 animate-fade-in">
              <div className="text-6xl mb-6">🎭</div>
              <h2 className="font-display text-3xl text-foreground">L'IA prépare votre scène...</h2>
              <div className="flex items-center justify-center gap-3 text-seer">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Génération du contexte unique</span>
              </div>
            </div>
          )}

          {phase === 'context' && !isGenerating && currentContext && (
            <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-seer/20 border border-seer/30 text-seer text-xs mb-2">
                  <Sparkles className="w-3 h-3" />
                  Généré par IA
                </div>
                <span className="block text-sm text-muted-foreground">Scène {gameState.sceneNumber}</span>
                <h2 className="font-display text-4xl text-foreground">{currentContext.title}</h2>
              </div>

              <Card className="p-8 bg-gradient-to-br from-seer/10 via-background to-background border-seer/30">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-display text-lg text-seer mb-2 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      Le Contexte
                    </h3>
                    <p className="text-foreground text-lg leading-relaxed">{currentContext.setting}</p>
                  </div>

                  <div>
                    <h3 className="font-display text-lg text-moon mb-3 flex items-center gap-2">
                      <Drama className="w-5 h-5" />
                      Les Personnages
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {currentContext.characters.map((char, i) => (
                        <span 
                          key={i}
                          className="px-3 py-1.5 rounded-full bg-moon/10 border border-moon/30 text-sm"
                        >
                          {gameState.players[i] || `Joueur ${i+1}`}: <span className="text-moon">{char}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-lg text-accent mb-3 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Les Contraintes
                    </h3>
                    <div className="space-y-2">
                      {gameState.constraints.map((constraint, i) => (
                        <div 
                          key={i}
                          className="px-4 py-2 rounded-lg bg-accent/10 border border-accent/30 text-sm"
                        >
                          {constraint}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              <div className="flex gap-4">
                <Button 
                  variant="outline"
                  onClick={generateContext}
                  disabled={isGenerating}
                  className="flex-1 border-seer/30 text-seer hover:bg-seer/10"
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Autre contexte
                </Button>
                <Button 
                  onClick={startScene}
                  className="flex-1 bg-seer hover:bg-seer/90 text-background font-display"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Lancer la scène !
                </Button>
              </div>
            </div>
          )}

          {phase === 'playing' && currentContext && (
            <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/50 text-green-400">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  EN SCÈNE
                </div>
                <h2 className="font-display text-3xl text-foreground">{currentContext.title}</h2>
              </div>

              <Card className="p-6 bg-background/50 border-border/50">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {gameState.constraints.map((constraint, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 rounded-full bg-accent/10 border border-accent/30 text-xs"
                      >
                        {constraint}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                <Button 
                  onClick={triggerTwist}
                  disabled={isGeneratingTwist}
                  variant="outline"
                  className="h-20 border-moon/30 text-moon hover:bg-moon/10 flex flex-col gap-1"
                >
                  {isGeneratingTwist ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span className="font-display text-sm">Génération...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-6 h-6" />
                      <span className="font-display">Rebondissement IA</span>
                    </>
                  )}
                </Button>
                <Button 
                  onClick={nextScene}
                  disabled={isGenerating}
                  variant="outline"
                  className="h-20 border-seer/30 text-seer hover:bg-seer/10 flex flex-col gap-1"
                >
                  {isGenerating ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <RefreshCw className="w-6 h-6" />
                  )}
                  <span className="font-display">Nouvelle scène</span>
                </Button>
              </div>

              <Button 
                onClick={endGame}
                variant="ghost"
                className="w-full text-muted-foreground hover:text-foreground"
              >
                Terminer la partie
              </Button>
            </div>
          )}

          {phase === 'twist' && (
            <div className="max-w-2xl mx-auto space-y-8 animate-fade-in">
              <div className="text-center">
                <div className="text-6xl mb-6 animate-bounce">🎭</div>
                <h2 className="font-display text-2xl text-moon mb-4">REBONDISSEMENT !</h2>
              </div>

              <Card className="p-8 bg-gradient-to-br from-moon/20 via-background to-background border-moon/50 text-center">
                <p className="text-xl text-foreground leading-relaxed font-display">
                  {gameState.currentTwist}
                </p>
              </Card>

              <Button 
                onClick={continueAfterTwist}
                className="w-full bg-moon hover:bg-moon/90 text-background font-display"
              >
                <Play className="w-4 h-4 mr-2" />
                Continuer la scène
              </Button>
            </div>
          )}

          {phase === 'end' && (
            <div className="max-w-md mx-auto text-center space-y-8 animate-fade-in">
              <div className="text-6xl">🎭✨</div>
              <h2 className="font-display text-4xl text-foreground">Bravo !</h2>
              <p className="text-lg text-muted-foreground">
                {gameState.sceneNumber} scène{gameState.sceneNumber > 1 ? 's' : ''} improvisée{gameState.sceneNumber > 1 ? 's' : ''} avec brio !
              </p>

              <ShareResult 
                gameName="Impro Théâtrale"
                score={gameState.sceneNumber * 50}
              />

              <div className="flex flex-col gap-3">
                <Button 
                  onClick={resetGame}
                  className="bg-seer hover:bg-seer/90 text-background font-display"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Nouvelle partie
                </Button>
                <Link to="/">
                  <Button variant="outline" className="w-full border-seer/30 text-seer hover:bg-seer/10">
                    Retour à la plateforme
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Impro;
